import {
  Box,
  Typography,
  Stack,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import TokenIcon from "@mui/icons-material/AccountBalanceWallet";
import { Message } from "../models/Message";
import { useAppSelector } from "../hooks/hooks";
import { format, toZonedTime } from "date-fns-tz";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

// Helper function to get icon for media type
const getMediaIcon = (type: string) => {
  switch (type) {
    case "image":
      return <ImageIcon fontSize="small" />;
    case "video":
      return <VideocamIcon fontSize="small" />;
    case "file":
      return <AttachFileIcon fontSize="small" />;
    case "gift":
      return <CardGiftcardIcon fontSize="small" />;
    case "token":
      return <TokenIcon fontSize="small" />;
    default:
      return <AttachFileIcon fontSize="small" />;
  }
};

// Helper component for media items
const MediaItem = ({
  type,
  content,
  isMobile,
}: {
  type: string;
  content: string;
  isMobile: boolean;
}) => {
  const getLabel = () => {
    switch (type) {
      case "image":
        return "Image";
      case "video":
        return "Video";
      case "file":
        return "File";
      case "gift":
        return "Gift Card";
      case "token":
        return "Token Transfer";
      default:
        return "Attachment";
    }
  };

  return (
    <Chip
      avatar={
        <Avatar sx={{ width: 24, height: 24 }}>{getMediaIcon(type)}</Avatar>
      }
      label={getLabel()}
      variant="outlined"
      size={isMobile ? "small" : "medium"}
      sx={{
        bgcolor: "background.paper",
        borderColor: "divider",
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
      }}
      onClick={() => console.log("Open", type, content)}
    />
  );
};

// Main ChatBubble component
const ChatBubble = ({ message }: { message: Message }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const mobileQuery = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const isMobileView = isMobile !== undefined ? isMobile : mobileQuery;
  const isIncoming = message.direction === "incoming";
  const { user } = useAppSelector((state) => state.user);
  const { timezone } = useAppSelector((state) => state.ui);

  // Parse message parts if using composite format
  const messageParts = message.parts || [
    { type: message.messageType || "text", content: message.content },
  ];

  /**
   * Parses and formats the timestamp according to the user's timezone
   */
  const formatTimestamp = (timestamp: string) => {
    try {
      // Default to UTC if timezone isn't set
      const userTimezone =
        timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Parse the timestamp (assuming it's in UTC from the server)
      const date = new Date(timestamp);

      // Convert to user's timezone
      const zonedDate = toZonedTime(date, userTimezone);

      // Format as "HH:mm" (24-hour format) or "h:mm a" (12-hour format)
      return format(zonedDate, isMobileView ? "h:mm a" : "HH:mm", {
        timeZone: userTimezone,
      });
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "Invalid time";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isIncoming ? "flex-start" : "flex-end",
        mb: isMobileView ? 1.5 : 2,
        px: isMobileView ? 1 : 2,
      }}
    >
      {/* Sender label (if present) */}
      {message.sender && (
        <Typography
          variant="caption"
          sx={{
            mb: 0.5,
            color: "text.secondary",
            fontSize: isMobileView ? "0.7rem" : "0.75rem",
          }}
        >
          {message.sender.toLowerCase() === user?.walletAddress?.toLowerCase()
            ? "Me"
            : message.sender}
        </Typography>
      )}

      {/* Message bubble */}
      <Box
        sx={{
          bgcolor: isIncoming ? "background.default" : "primary.main",
          color: isIncoming ? "text.primary" : "primary.contrastText",
          borderRadius: isIncoming
            ? isMobileView
              ? "12px 12px 12px 0"
              : "18px 18px 18px 0"
            : isMobileView
            ? "12px 12px 0 12px"
            : "18px 18px 0 18px",
          p: isMobileView ? 1.5 : 2,
          maxWidth: isMobileView ? "90%" : "80%",
          position: "relative",
        }}
      >
        {/* Message content */}
        <Stack spacing={isMobileView ? 0.5 : 1}>
          {messageParts.map((part, index) => {
            if (part.type === "text") {
              return (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{ fontSize: isMobileView ? "0.875rem" : "1rem" }}
                >
                  {part.content}
                </Typography>
              );
            }
            if (part.type === "token") {
              return (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{ fontSize: isMobileView ? "0.875rem" : "1rem" }}
                >
                  {part.content.split("*")[0].indexOf("req") !== -1
                    ? "REQUESTING"
                    : "SENT"}{" "}
                  {part.content.split("*")[1]} {part.content.split("*")[2]}
                </Typography>
              );
            }
            return (
              <MediaItem
                key={index}
                type={part.type}
                content={part.content}
                isMobile={isMobileView}
              />
            );
          })}
        </Stack>

        {/* Timestamp and status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: isMobileView ? 0.5 : 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              mr: 0.5,
              fontSize: isMobileView ? "0.6rem" : "0.75rem",
            }}
          >
            {formatTimestamp(message.timestamp)}
          </Typography>
          {message.direction === "outgoing" &&
            (message.status === "read" ? (
              <CheckCircleIcon
                sx={{ fontSize: isMobileView ? 12 : 14, color: "#4fc3f7" }}
              />
            ) : message.status === "delivered" ? (
              <CheckCircleOutlineIcon
                sx={{ fontSize: isMobileView ? 12 : 14 }}
              />
            ) : (
              <CheckCircleOutlineIcon
                sx={{ fontSize: isMobileView ? 12 : 14, opacity: 0.5 }}
              />
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatBubble;
